import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useReducer, useRef } from 'react';
import { FocusMode } from '@skbkontur/react-ui-validations';
import { getIdentityDocumentRequest, getPatchOperations, useStaffServiceClient, useStaffWidgetsClient, } from '@skbkontur/staff-common';
import { useStaffServiceMetrics } from '../MetricsProvider';
import { convertEmployeesToMatchFields, getChangedFields, handleDocumentSaveError, logRequestError, toEditState, toEmployee, toMatchEmployeeRequest, toPatchEmployee, } from './EditSidePage.helpers';
import { stateReducer } from './EditSidePage.reducer';
import { getMainTabValidation, getPaymentTabValidation } from './EditSidePage.validators';
const featureFlagKey = 'SelectorWidget.EmployeeSfrNumberField';
export const EmployeeContext = createContext({});
export const EmployeeProvider = ({ employee, children, references, listType = 'employee', fields, employerId, onClose, onReloadCurrentPage, }) => {
    const trackAction = useStaffServiceMetrics();
    const client = useStaffServiceClient();
    const staffWidgetsClient = useStaffWidgetsClient();
    const refValidation = useRef(null);
    const validate = () => refValidation.current?.validate({ focusMode: FocusMode.ErrorsAndWarnings });
    const [state, dispatch] = useReducer(stateReducer, toEditState(employee, references));
    const isEditPage = !!employee;
    const trackEmployeeEdit = (action, options) => {
        trackAction('employee', action, {
            ...options,
            target: employee?.id || 'new',
            listType,
            employeeId: employee?.id,
        });
    };
    const handleTabChange = (tab) => {
        trackEmployeeEdit('employeeSwitchTab', { tab });
        dispatch({ type: 'changeTabStatus', payload: tab });
    };
    const switchTab = async () => {
        const hasMainValidationErrors = getMainTabValidation(state, fields, references).some((el) => el !== null);
        const hasPaymentValidationErrors = getPaymentTabValidation(state, fields).some((el) => el !== null);
        if (hasPaymentValidationErrors && !hasMainValidationErrors) {
            handleTabChange('bank');
            return;
        }
        handleTabChange('main');
    };
    const saveEmployee = async () => {
        try {
            const isValid = await refValidation.current?.validate();
            if (!isValid) {
                await switchTab();
                trackEmployeeEdit('submitEmployeeEdit', { valid: false });
                dispatch({ type: 'changeFormStatus', payload: 'edit' });
                return;
            }
            const normalizedEmployee = toEmployee(state);
            if (isEditPage) {
                const restrictedProfileKeys = ['activePeriod', 'registrationAddress', 'homeAddress'];
                const operations = getPatchOperations(toPatchEmployee(employee, fields), toPatchEmployee(normalizedEmployee, fields), (key) => restrictedProfileKeys.includes(key));
                const patchEmployee = listType === 'employee' ? client.patchEmployee.bind(client) : client.patchClient.bind(client);
                if (operations.length > 0) {
                    await patchEmployee(employerId, employee.id, operations);
                    trackEmployeeEdit('submitEmployeeEdit', { valid: true, fields: getChangedFields(operations) });
                }
            }
            else {
                const createEmployee = listType === 'employee' ? client.createEmployee.bind(client) : client.createClient.bind(client);
                const { id } = await createEmployee(employerId, {
                    profile: normalizedEmployee.profile,
                    requisites: normalizedEmployee.requisites,
                });
                normalizedEmployee.id = id;
                await saveIdentityDocuments(id);
            }
            dispatch({ type: 'changeFormStatus', payload: 'edit' });
            onReloadCurrentPage();
            !state.matchFields && onClose();
        }
        catch (error) {
            dispatch({ type: 'changeFormStatus', payload: 'error' });
        }
    };
    const saveIdentityDocuments = async (employeeId) => {
        try {
            const createIdentityDocument = listType === 'employee'
                ? client.createIdentityDocument.bind(client)
                : client.createClientsIdentityDocument.bind(client);
            const docPromises = state.identityDocuments.map((doc) => {
                return createIdentityDocument(employerId, employeeId, getIdentityDocumentRequest(doc));
            });
            await Promise.all(docPromises);
        }
        catch (error) {
            handleDocumentSaveError();
        }
    };
    const getFeatureFlags = async () => {
        try {
            const res = await staffWidgetsClient.getFeatureFlags();
            if (res.status === 'success') {
                return res.value.items;
            }
        }
        catch (error) {
            logRequestError(error);
        }
        return [];
    };
    const getMatchFields = async () => {
        try {
            const shouldFindMatches = state.snils || state.inn || (state.lastName && state.birthday);
            if (listType === 'client' || !shouldFindMatches) {
                return null;
            }
            const res = await client.matchEmployee(employerId, toMatchEmployeeRequest(state, employee));
            const candidates = (res.candidates || []).filter((el) => el.id !== employee?.id);
            if (candidates.length > 0) {
                trackEmployeeEdit('employeeMatchFound', {
                    employeeId: employee?.id,
                    context: employee?.id ? 'editEmployee' : 'createEmployee',
                });
            }
            return convertEmployeesToMatchFields(state, candidates);
        }
        catch (error) {
            logRequestError(error);
            return null;
        }
    };
    const init = async () => {
        const matchFields = await getMatchFields();
        dispatch({ type: 'changeMatchValue', payload: matchFields });
    };
    const checkForm = async () => {
        const matchFields = await getMatchFields();
        dispatch({ type: 'changeMatchValue', payload: matchFields });
        if (isEditPage || !matchFields) {
            dispatch({ type: 'changeFormStatus', payload: 'saving' });
            return;
        }
        refValidation.current?.validate();
        dispatch({ type: 'changeFormStatus', payload: 'edit' });
    };
    useEffect(() => {
        init();
    }, []);
    useEffect(() => {
        if (state.formStatus === 'checking') {
            checkForm();
        }
        if (state.formStatus === 'saving') {
            saveEmployee();
        }
    }, [state.formStatus]);
    useEffect(() => {
        isEditPage && state.formStatus === 'edit' && refValidation.current?.validate();
    }, [state.tabStatus, state.matchFields, state.formStatus]);
    return (_jsx(EmployeeContext.Provider, { value: {
            state,
            dispatch,
            references,
            isEditPage,
            trackEmployeeEdit,
            listType,
            fields,
            onClose,
            onReloadCurrentPage,
            refValidation,
            hasMatch: !!state.matchFields,
            initialEmployee: employee,
            employerId,
            formStatus: state.formStatus,
            handleFormStatusChange: (payload) => dispatch({ type: 'changeFormStatus', payload }),
            tabStatus: state.tabStatus,
            handleTabChange,
            validate,
        }, children: children }));
};
